/**
 * 边框 Design Token 系统
 * 定义完整的边框样式规范
 */

import type { BorderTokens } from './types';

// 边框 Token 定义
export const borderTokens: BorderTokens = {
  // 圆角半径
  radius: {
    none: 0,        // 0px - 无圆角
    xs: 2,          // 0.125rem - 极小圆角
    sm: 4,          // 0.25rem - 小圆角
    base: 6,        // 0.375rem - 标准圆角
    md: 8,          // 0.5rem - 中等圆角
    lg: 12,         // 0.75rem - 大圆角
    xl: 16,         // 1rem - 超大圆角
    '2xl': 24,      // 1.5rem - 特大圆角
    full: 9999,     // 完全圆角 (圆形/椭圆)
  },
  
  // 边框宽度
  width: {
    none: 0,        // 0px - 无边框
    thin: 1,        // 1px - 细边框
    base: 1,        // 1px - 标准边框 (默认)
    thick: 2,       // 2px - 粗边框
    thicker: 4,     // 4px - 更粗边框
  },
};

// 边框样式组合
export const borderStyles = {
  // 标准边框样式
  default: {
    width: borderTokens.width.base,
    style: 'solid',
    color: 'var(--border-color)',
  },
  
  // 细边框样式
  thin: {
    width: borderTokens.width.thin,
    style: 'solid',
    color: 'var(--border-color)',
  },
  
  // 粗边框样式
  thick: {
    width: borderTokens.width.thick,
    style: 'solid',
    color: 'var(--border-color)',
  },
  
  // 虚线边框样式
  dashed: {
    width: borderTokens.width.base,
    style: 'dashed',
    color: 'var(--border-color)',
  },
  
  // 点线边框样式
  dotted: {
    width: borderTokens.width.base,
    style: 'dotted',
    color: 'var(--border-color)',
  },
  
  // 无边框样式
  none: {
    width: borderTokens.width.none,
    style: 'none',
    color: 'transparent',
  },
};

// 圆角使用规范
export const radiusUsage = {
  none: '表格、分割线、严格几何形状',
  xs: '小标签、徽章、状态指示器',
  sm: '按钮、输入框、小卡片',
  base: '标准卡片、对话框、下拉菜单',
  md: '大卡片、面板、容器',
  lg: '图片容器、媒体卡片、特殊容器',
  xl: '大型容器、页面区块、特殊面板',
  '2xl': '装饰性容器、特大面板、品牌元素',
  full: '头像、圆形按钮、标签、徽章',
};

// 边框宽度使用规范
export const widthUsage = {
  none: '无边框需求、透明边框占位',
  thin: '分割线、表格边框、细节边框',
  base: '标准组件边框、卡片边框、输入框边框',
  thick: '强调边框、选中状态、重要容器',
  thicker: '特殊强调、品牌边框、装饰性边框',
};

// 响应式边框系统
export const responsiveBorders = {
  // 移动端边框调整
  mobile: {
    radius: {
      ...borderTokens.radius,
      base: 4,        // 减小标准圆角
      md: 6,          // 减小中等圆角
      lg: 8,          // 减小大圆角
      xl: 12,         // 减小超大圆角
      '2xl': 16,      // 减小特大圆角
    },
    width: borderTokens.width, // 边框宽度保持不变
  },
  
  // 平板端边框调整
  tablet: {
    radius: {
      ...borderTokens.radius,
      base: 5,        // 略微减小标准圆角
      md: 7,          // 略微减小中等圆角
      lg: 10,         // 略微减小大圆角
      xl: 14,         // 略微减小超大圆角
      '2xl': 20,      // 略微减小特大圆角
    },
    width: borderTokens.width,
  },
  
  // 桌面端边框 (默认)
  desktop: borderTokens,
};

// 组件特定边框配置
export const componentBorders = {
  // 按钮边框
  button: {
    primary: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
    secondary: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
    ghost: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
    text: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.none,
      style: 'none',
    },
  },
  
  // 输入框边框
  input: {
    default: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
    focus: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.thick,
      style: 'solid',
    },
    error: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
  },
  
  // 卡片边框
  card: {
    default: {
      radius: borderTokens.radius.md,
      width: borderTokens.width.base,
      style: 'solid',
    },
    hover: {
      radius: borderTokens.radius.md,
      width: borderTokens.width.base,
      style: 'solid',
    },
  },
  
  // 对话框边框
  modal: {
    default: {
      radius: borderTokens.radius.lg,
      width: borderTokens.width.none,
      style: 'none',
    },
  },
  
  // 下拉菜单边框
  dropdown: {
    default: {
      radius: borderTokens.radius.base,
      width: borderTokens.width.base,
      style: 'solid',
    },
  },
  
  // 表格边框
  table: {
    cell: {
      radius: borderTokens.radius.none,
      width: borderTokens.width.thin,
      style: 'solid',
    },
    header: {
      radius: borderTokens.radius.none,
      width: borderTokens.width.base,
      style: 'solid',
    },
  },
};

// 导出边框常量，用于 SCSS 变量兼容
export const borderConstants = {
  // 圆角半径
  RADIUS_NONE: borderTokens.radius.none,
  RADIUS_XS: borderTokens.radius.xs,
  RADIUS_SM: borderTokens.radius.sm,
  RADIUS_BASE: borderTokens.radius.base,
  RADIUS_MD: borderTokens.radius.md,
  RADIUS_LG: borderTokens.radius.lg,
  RADIUS_XL: borderTokens.radius.xl,
  RADIUS_2XL: borderTokens.radius['2xl'],
  RADIUS_FULL: borderTokens.radius.full,
  
  // 边框宽度
  WIDTH_NONE: borderTokens.width.none,
  WIDTH_THIN: borderTokens.width.thin,
  WIDTH_BASE: borderTokens.width.base,
  WIDTH_THICK: borderTokens.width.thick,
  WIDTH_THICKER: borderTokens.width.thicker,
} as const;